import type { UIPartyCharacter, UIInventoryItem, UIItemContainerKind } from '@sorrem-client/types';
import { getStoredLoadout, resolveWearerDisciplines, getWieldedLoadout, type Equipment } from '@sorrem/shared';
import { applyItemMove, fromEquipmentSlots, resolveComparedEquipment, toEquipmentSlots } from '@sorrem-client/ui/shared/equipment-slots';
import { LOADOUT_SLOTS } from '@sorrem-client/ui/shared/loadout-slots';
import { toEquipment, toUIInventoryItem, withGearPotency } from '@sorrem-client/ui/shared/inventory-item';

export type CharacterHomeTransferItem = UIInventoryItem | Equipment;

/** For a carried or stored slot, where an empty one is undefined. */
function coerceToInventoryItem(item: CharacterHomeTransferItem | null | undefined): UIInventoryItem | undefined {
    if (!item) {
        return undefined;
    }

    if ('type' in item) {
        return item;
    }

    return toUIInventoryItem(item);
}

/** For an equipment slot, where an empty one is null (#336). */
function coerceToEquipmentItem(item: CharacterHomeTransferItem | null | undefined): Equipment | null {
    if (!item) {
        return null;
    }

    // Only UI items have a `type` (see coerceToInventoryItem).
    if (!('type' in item)) {
        return item;
    }

    return toEquipment(item);
}

/**
 * The character as a move leaves them, for Character Home's optimistic view
 * (see `applyItemMove`, which the move rules read too). A move out of range,
 * onto its own slot, or of an item that isn't there leaves them as they are.
 */
export function applyCharacterHomeMove(
    player: UIPartyCharacter,
    selection: { itemId: string; container: UIItemContainerKind },
    target: { container: UIItemContainerKind; index: number },
): UIPartyCharacter {
    const targetSize = target.container === 'inventory'
        ? player.maxCarriedSlots
        : target.container === 'storage'
            ? player.maxStoredSlots
            : LOADOUT_SLOTS.length;
    if (target.index < 0 || target.index >= targetSize) {
        return player;
    }
    const outcome = applyItemMove(
        { equipped: getStoredLoadout(player), carriedItems: player.carriedItems, storedItems: player.storedItems, carriedCapacity: player.maxCarriedSlots },
        selection,
        target,
    );
    const sourceEntry = findCharacterHomeItem(player, selection.itemId);
    if (outcome === null || !sourceEntry || isSameSlot(player, selection, target)) {
        return player;
    }

    // With no carried slot free for the off-hand item, the view shows the move anyway; the hands rule refuses it.
    return {
        ...player,
        carriedItems: outcome.carried.map(coerceToInventoryItem),
        storedItems: outcome.stored.map(coerceToInventoryItem),
        equipped: fromEquipmentSlots(outcome.equipment.map(coerceToEquipmentItem)),
    };
}

/** Whether the target is the slot the selected item already sits in. */
function isSameSlot(
    player: UIPartyCharacter,
    selection: { itemId: string; container: UIItemContainerKind },
    target: { container: UIItemContainerKind; index: number },
): boolean {
    if (selection.container !== target.container) {
        return false;
    }
    const items = selection.container === 'inventory'
        ? player.carriedItems
        : selection.container === 'storage' ? player.storedItems : toEquipmentSlots(getStoredLoadout(player));
    return items[target.index]?.id === selection.itemId;
}

/** Finds a carried, stored, or equipped item by id, wherever it currently sits. */
export function findCharacterHomeItem(player: UIPartyCharacter, itemId: string): CharacterHomeTransferItem | undefined {
    return player.carriedItems.find((entry) => entry?.id === itemId)
        ?? player.storedItems.find((entry) => entry?.id === itemId)
        ?? toEquipmentSlots(getStoredLoadout(player)).find((entry) => entry?.id === itemId) ?? undefined;
}

/** What an item card shows for a Character Home selection. */
export interface CharacterHomeItemCard {
    readonly item: UIInventoryItem;
    /** Worn or wielded: it has to be taken off before it can be traded. */
    readonly isEquipped: boolean;
    /** For a carried or stored item, the equipped items it compares against (`resolveComparedEquipment`); none for an equipped one. */
    readonly compared: readonly Equipment[];
}

/**
 * The card for a selected carried, stored or equipped item (#204): an
 * equipped item shows as it is, and any other is compared like for like
 * (`resolveComparedEquipment`): a sword in the bags with the sword in hand
 * even when the other hand is free, and with both hands when both hold
 * something (#341). A carried or stored item says whether the character
 * falls short of its requirement (#250); an equipped one comes as wielded,
 * weakened or not.
 */
export function resolveCharacterHomeItemCard(player: UIPartyCharacter, itemId: string): CharacterHomeItemCard | undefined {
    const equipmentSlots = toEquipmentSlots(getWieldedLoadout(player));
    const held = player.carriedItems.find((entry) => entry?.id === itemId)
        ?? player.storedItems.find((entry) => entry?.id === itemId);
    if (held) {
        return { item: withGearPotency(held, resolveWearerDisciplines(player)), isEquipped: false, compared: resolveComparedEquipment(held, equipmentSlots) };
    }
    const equipped = equipmentSlots.find((entry) => entry?.id === itemId && entry.itemDefinitionId.length > 0);
    return equipped ? { item: toUIInventoryItem(equipped), isEquipped: true, compared: [] } : undefined;
}
